import type { DoozMove, DoozView } from "@bing/engine-dooz";
import { DEFAULT_DOOZ_SEAT0_COLOR, DOOZ_ONLINE_ROOM } from "@bing/shared";
import type { ReactNode } from "react";
import { fa } from "../i18n/fa";
import { DoozColorContext, DoozTable } from "../screens/DoozTable";
import { useSession } from "../store/context";
import { DoozEndOverlay } from "./DoozGame";
import { OnlineGame } from "./OnlineGame";
import { otherHuman } from "./seat-identity";

/**
 * Online دوز — a thin wrapper over the shared `OnlineGame` shell (public quick-match OR a host's
 * private table). Two seats, perfect information: no card deck, no team overlay.
 *
 * ⚠️ THE PIECE COLOUR COMES OFF THE TABLE'S OWN SETTINGS, NOT OFF THIS DEVICE (operator,
 * 2026-09-03). `settings.doozColor` names the colour SEAT 0 plays in and the other seat takes the
 * remaining one, so both players see the same disc in the same cell. A PRIVATE host chose it (they
 * are seat 0); a PUBLIC table has nobody to ask for the stranger who joins, so the server forces
 * the default and neither player is asked.
 *
 * It used to be a purely viewer-side preference with four options — each screen rendering its own
 * pieces in whatever that device had picked — which meant two people could look at the same cell
 * and name two different colours, and a screenshot passed between them disagreed.
 *
 * ONE public room (`dooz-online`) and one private one, so the shell's own `-online` → `-private`
 * derivation is correct here and nothing has to be stated.
 */
export function DoozOnlineGame(): ReactNode {
  const profile = useSession((s) => s.profile);
  return (
    <OnlineGame<DoozMove, DoozView>
      gameId="dooz"
      room={DOOZ_ONLINE_ROOM}
      title={fa.dooz.title}
      renderTable={({ view, legal, send, leave, players, timer, settings }) => {
        return (
          <DoozColorContext.Provider value={settings?.doozColor ?? DEFAULT_DOOZ_SEAT0_COLOR}>
            <DoozTable
              view={view}
              legal={legal}
              onMove={send}
              onLeave={leave}
              self={profile}
              opponent={otherHuman(players, view.you)}
              /* The per-turn ring. `totalMs` rides the frame so the ring draws the window the
                 SERVER actually armed rather than the configured length — the two differ whenever
                 a window is stretched, and assuming the setting is what made تخته نرد's ring look
                 frozen. دوز never stretches one, but reading the frame costs nothing and cannot
                 go stale. */
              timer={
                timer
                  ? {
                      turn: timer.turn,
                      you: timer.you,
                      totalSec: timer.totalSec,
                    }
                  : null
              }
              // EVERY online table gets the chat toggle (OnlineGame mounts it iff `lobby?.roomId`,
              // which the server sends on every lobby frame), so its slot in the header is kept
              // from the FIRST frame — on a resume the state frame lands before the lobby frame,
              // and a slot that waited for it would move «دوز» (حکم's rule, HokmOnlineGame).
              chat
            />
          </DoozColorContext.Provider>
        );
      }}
      renderWin={({
        result,
        view,
        again,
        leave,
        players,
        againDisabled,
        againHint,
        againHidden,
      }) => {
        return (
          <DoozEndOverlay
            result={result}
            view={view}
            self={profile}
            opponent={otherHuman(players, view.you)}
            onAgain={again}
            againDisabled={againDisabled}
            againHint={againHint}
            againHidden={againHidden}
            onLeave={leave}
          />
        );
      }}
    />
  );
}
